import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { http } from "../../lib/api";
import PremiumLayout from "./PremiumLayout";
import BeforeAfterSlider from "./BeforeAfterSlider";

// Fallback before/after images that pair well for any project
const FALLBACK_BEFORE = "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1600&auto=format&fit=crop&q=80";
const FALLBACK_AFTER  = "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1600&auto=format&fit=crop&q=80";

export default function PremiumProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    http.get("/marketing/site").then(({ data }) => {
      const projs = data.projects || [];
      const p = projs.find((x) => x.id === id);
      setProject(p || null);
      setRelated(projs.filter((x) => x.id !== id).slice(0, 3));
    }).catch(() => {});
  }, [id]);

  if (!project) {
    return (
      <PremiumLayout>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-32 text-center">
          <div className="mono gold">Loading…</div>
        </div>
      </PremiumLayout>
    );
  }

  const photos = project.photos || [];
  const hero = photos[0] || FALLBACK_AFTER;
  const gallery = photos.slice(1);

  return (
    <PremiumLayout>
      {/* Hero */}
      <section className="relative h-[70vh] w-full overflow-hidden" data-testid="case-study-hero">
        <img src={hero} alt={project.title} className="absolute inset-0 w-full h-full object-cover ken-burns" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/50 via-[#0A0A0A]/30 to-[#0A0A0A]" />
        <div className="relative h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-14">
          <div className="mono gold">{project.category || "Project"} · {project.location || "—"}</div>
          <h1 className="serif text-5xl md:text-7xl leading-[1.02] cream mt-4 max-w-4xl">{project.title}</h1>
        </div>
      </section>

      {/* Meta strip */}
      <section className="border-y border-[#1F1F1F]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            ["Category", project.category || "—"],
            ["Location", project.location || "—"],
            ["Studio", "Dream Home Planners"],
            ["Delivery", "Turnkey"],
          ].map(([k, v]) => (
            <div key={k}>
              <div className="mono text-[#7A7267]">{k}</div>
              <div className="cream mt-1">{v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Narrative */}
      <section className="max-w-[1000px] mx-auto px-6 md:px-10 py-24" data-testid="case-study-narrative">
        <div className="mono gold">Project Notes</div>
        <p className="serif text-2xl md:text-3xl leading-[1.35] cream mt-6">
          {project.description || "A design that balances structural honesty with warm, tactile materials — building a space you inhabit rather than just occupy."}
        </p>
        <div className="mt-14 grid md:grid-cols-2 gap-10 text-[#DDD5C4] leading-relaxed">
          <div>
            <div className="mono gold">Blueprint &amp; Structure</div>
            <p className="mt-4 text-sm">
              Cast-in-situ RCC frame designed for optimal spans and natural cross-ventilation. All
              structural elements coordinated with MEP services from the very first draft.
            </p>
          </div>
          <div>
            <div className="mono gold">Interior &amp; Material</div>
            <p className="mt-4 text-sm">
              Curated palette of hand-laid stone, oak veneers, and matte metalwork.
              Bespoke lighting, modular kitchen, and custom joinery installed in-house.
            </p>
          </div>
        </div>
      </section>

      {/* Before / After slider */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24" data-testid="case-study-beforeafter">
        <div className="mono gold">Before &amp; After</div>
        <h2 className="serif text-4xl md:text-5xl leading-[1.05] cream mt-4">
          From civil frame <span className="gold italic">to lived-in warmth.</span>
        </h2>
        <p className="text-[#B8B0A0] max-w-2xl mt-4 text-sm">
          Drag the slider to compare the raw civil handover against the final furnished interior.
        </p>
        <div className="mt-10">
          <BeforeAfterSlider
            before={photos[1] || FALLBACK_BEFORE}
            after={photos[0] || FALLBACK_AFTER}
            captionBefore="Civil handover"
            captionAfter="Final interior"
            testId="case-study-slider"
          />
        </div>
      </section>

      {/* Gallery */}
      {gallery.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24" data-testid="case-study-gallery">
          <div className="mono gold">Gallery</div>
          <h2 className="serif text-4xl md:text-5xl cream mt-4">Spaces</h2>
          <div className="mt-10 grid md:grid-cols-6 gap-4">
            {gallery.map((src, i) => (
              <div key={`${src}-${i}`} className={`overflow-hidden border border-[#1F1F1F] ${
                i % 5 === 0 ? "md:col-span-4 aspect-[16/10]" :
                i % 5 === 1 ? "md:col-span-2 aspect-square" :
                i % 5 === 2 ? "md:col-span-2 aspect-square" :
                i % 5 === 3 ? "md:col-span-3 aspect-[4/3]" :
                              "md:col-span-3 aspect-[4/3]"
              }`}>
                <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1.4s]" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Related */}
      {related.length > 0 && (
        <section className="border-t border-[#1F1F1F]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
            <div className="mono gold">More work</div>
            <h2 className="serif text-4xl md:text-5xl cream mt-4">Continue the tour</h2>
            <div className="mt-10 grid md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link key={p.id} to={`/projects/${p.id}`} className="group relative aspect-[4/3] overflow-hidden border border-[#1F1F1F]">
                  <img src={(p.photos || [])[0]} alt={p.title}
                       className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.4s]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/90 via-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-5">
                    <div className="mono gold">{p.category || "Project"}</div>
                    <div className="serif text-2xl cream mt-1">{p.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </PremiumLayout>
  );
}
